import {
  ChangeDetectionStrategy,
  Component,
  type OnDestroy,
} from '@angular/core';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';
import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CURATED_FONTS,
  DEFAULT_FONT_FAMILY,
  initVersionedSchema,
} from '@principle-theorem/editor';
import { FeeScheduleFacade } from '@principle-theorem/ng-clinical-charting/store';
import { BillingEntityControls } from '@principle-theorem/ng-principle';
import {
  CurrentPracticeScope,
  DocumentSettingsFormGroup,
  OrganisationService,
  PdfService,
} from '@principle-theorem/ng-principle-shared';
import {
  ImageSize,
  TrackByFunctions,
  TypedFormArray,
  TypedFormControl,
  TypedFormGroup,
  formControlChanges$,
  getImageAtSize,
  type IBreadcrumb,
} from '@principle-theorem/ng-shared';
import {
  Address,
  Brand,
  FeeSchedule,
  Practice,
} from '@principle-theorem/principle-core';
import {
  FONT_SIZE_PRESETS,
  FeeScheduleScope,
  IAddressMetadata,
  IBillingEntity,
  IFeeSchedule,
  IFeeScheduleGroup,
  IPDFContent,
  LINE_HEIGHT_PRESETS,
  MARGIN_PRESETS,
  ORIENTATION_PRESETS,
  PAPER_SIZE_PRESETS,
  PARAGRAPH_SPACING_PRESETS,
  type IGeoCoordinates,
  type IPractice,
  type IPracticeSettings,
} from '@principle-theorem/principle-core/interfaces';
import {
  DEFAULT_TIMEZONE,
  DocumentReference,
  Firestore,
  TIMEZONES,
  filterUndefined,
  isSameRef,
  shareReplayCold,
  slugify,
  VALID_URL,
  snapshot,
  titlecase,
  type Timezone,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { GeneralPracticeHelpers } from './general-practice.helpers';
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  from,
  of,
  type Observable,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';

type PracticeOptions = Pick<
  IPractice,
  'name' | 'email' | 'phone' | 'websiteUrl' | 'settings' | 'billingEntities'
>;

type IPracticeFormData = PracticeOptions & { address: IAddressMetadata };

interface IFeeScheduleSummary {
  scope: FeeScheduleScope;
  scopeName: string;
  feeScheduleRef: DocumentReference<IFeeSchedule>;
  feeScheduleName: string;
}

@Component({
  selector: 'pr-general-practice',
  templateUrl: './general-practice.component.html',
  styleUrls: ['./general-practice.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GeneralPracticeComponent implements OnDestroy {
  private _onDestroy$: Subject<void> = new Subject();
  trackByTimezone = TrackByFunctions.variable<Timezone>();
  isSameFeeSchedule = isSameRef;
  practice$: Observable<WithRef<IPractice>>;
  curatedFonts = CURATED_FONTS;
  defaultFontFamily = DEFAULT_FONT_FAMILY;

  timezones: Timezone[] = TIMEZONES;
  coordinates$ = new BehaviorSubject<IGeoCoordinates | undefined>(undefined);
  websiteUrlValid$ = new BehaviorSubject<'empty' | 'valid' | 'invalid'>(
    'empty'
  );

  feeScheduleGroups$: Observable<IFeeScheduleGroup[]>;
  defaultFeeSchedule$: Observable<WithRef<IFeeSchedule>>;
  unselectedFeeScheduleLabel$: Observable<string>;
  selectedFeeScheduleLabel$: Observable<string>;
  staleFeeScheduleSelected$: Observable<boolean>;
  billingEntityControls: BillingEntityControls;
  billingEntitiesEnabled$: Observable<boolean>;
  logoUploadPath$: Observable<string>;
  practiceLogoUrl$: Observable<string | undefined>;

  documentSettingsForm = new DocumentSettingsFormGroup();

  fontSizePresets = FONT_SIZE_PRESETS;
  lineHeightPresets = LINE_HEIGHT_PRESETS;
  paragraphSpacingPresets = PARAGRAPH_SPACING_PRESETS;
  marginPresets = MARGIN_PRESETS;
  paperSizePresets = PAPER_SIZE_PRESETS;
  orientationPresets = ORIENTATION_PRESETS;

  settingsForm = new TypedFormGroup<
    Pick<IPracticeSettings, 'timezone' | 'defaultFeeSchedule'>
  >({
    timezone: new TypedFormControl(DEFAULT_TIMEZONE, Validators.required),
    defaultFeeSchedule: new TypedFormControl<
      DocumentReference<IFeeSchedule> | undefined
    >(undefined),
  });
  form = new TypedFormGroup<IPracticeFormData>({
    name: new TypedFormControl('', Validators.required),
    address: new TypedFormControl<IAddressMetadata>({ address: '' }, [
      this._addressRequiredValidator(),
    ]),
    email: new TypedFormControl('', [Validators.required, Validators.email]),
    phone: new TypedFormControl('', Validators.required),
    websiteUrl: new TypedFormControl<string | undefined>(undefined, [
      this._websiteUrlValidator(),
    ]),
    settings: this.settingsForm,
    billingEntities: new TypedFormArray<IBillingEntity>([]),
  });
  breadcrumbs$: Observable<IBreadcrumb[]>;

  constructor(
    private _snackBar: MatSnackBar,
    private _storage: Storage,
    private _currentPractice: CurrentPracticeScope,
    private _feeSchedules: FeeScheduleFacade,
    private _organisation: OrganisationService,
    public pdfService: PdfService
  ) {
    this.practice$ = this._currentPractice.doc$.pipe(filterUndefined());
    this.practice$
      .pipe(take(1), takeUntil(this._onDestroy$))
      .subscribe((practice) => {
        this.billingEntityControls.initBillingEntityControls(practice, {
          emitEvent: false,
        });

        this.documentSettingsForm.setFromSettings(
          practice.settings?.print?.documentSettings
        );

        return this.form.patchValue({
          ...practice,
          address: Address.normaliseAddress(practice),
        });
      });

    this.practice$
      .pipe(
        take(1),
        switchMap(async (practice) => {
          const brandRef = Practice.brandRef(practice);
          const organisationRef = Brand.organisationRef({ ref: brandRef });
          return {
            organisation: await Firestore.getDoc(organisationRef),
            brand: await Firestore.getDoc(brandRef),
            practice,
          };
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe(({ organisation, brand, practice }) =>
        this._feeSchedules.loadFeeSchedules({ organisation, brand, practice })
      );

    this.feeScheduleGroups$ = this._feeSchedules.feeScheduleGroups$;
    this.defaultFeeSchedule$ = this.practice$.pipe(
      switchMap((practice) => {
        const brandRef = Practice.brandRef(practice);
        const organisationRef = Brand.organisationRef({ ref: brandRef });
        return combineLatest([
          Firestore.safeDoc$(FeeSchedule.defaultRef(practice)),
          this.feeScheduleGroups$,
        ]).pipe(
          switchMap(([practiceFeeSchedule, groups]) => {
            const isInGroups =
              practiceFeeSchedule &&
              groups
                .flatMap((group) => group.schedules)
                .some((schedule) => isSameRef(schedule, practiceFeeSchedule));
            if (isInGroups) {
              return of(practiceFeeSchedule);
            }
            return Firestore.doc$(FeeSchedule.defaultRef({ ref: organisationRef }));
          })
        );
      })
    );
    this.unselectedFeeScheduleLabel$ = combineLatest([
      this.feeScheduleGroups$,
      this.defaultFeeSchedule$,
    ]).pipe(
      map(([groups, feeSchedule]) => {
        const label = this._getFeeScheduleLabel(groups, feeSchedule.ref);
        return `Auto - ${label ?? feeSchedule.name ?? 'Unknown'}`;
      })
    );
    this.selectedFeeScheduleLabel$ = combineLatest([
      this.feeScheduleGroups$,
      this.unselectedFeeScheduleLabel$,
      formControlChanges$(this.settingsForm.controls.defaultFeeSchedule),
    ]).pipe(
      switchMap(([schedules, unselectedLabel, selected]) =>
        this._resolveSelectedFeeScheduleLabel(
          schedules,
          unselectedLabel,
          selected
        )
      )
    );
    this.staleFeeScheduleSelected$ = combineLatest([
      this.feeScheduleGroups$,
      formControlChanges$(this.settingsForm.controls.defaultFeeSchedule),
    ]).pipe(
      map(([groups, selected]) => {
        if (!selected) {
          return false;
        }
        const allSchedules = groups.flatMap((group) => group.schedules);
        return !allSchedules.some((schedule) => isSameRef(schedule, selected));
      })
    );

    formControlChanges$(this.form.controls.address)
      .pipe(debounceTime(500), takeUntil(this._onDestroy$))
      .subscribe((address) => this.coordinates$.next(address?.coordinates));

    formControlChanges$(this.form.controls.websiteUrl)
      .pipe(debounceTime(300), takeUntil(this._onDestroy$))
      .subscribe((url) => {
        if (!url) {
          this.websiteUrlValid$.next('empty');
        } else if (this.form.controls.websiteUrl.valid) {
          this.websiteUrlValid$.next('valid');
        } else {
          this.websiteUrlValid$.next('invalid');
        }
      });

    this.breadcrumbs$ = this.practice$.pipe(
      map((practice) => [
        { label: 'Settings', path: '../../../' },
        { label: practice.name },
        { label: 'General Settings' },
      ])
    );

    this.billingEntityControls = new BillingEntityControls(
      this.billingEntities
    );

    const brand$ = this._organisation.brand$.pipe(filterUndefined());

    this.billingEntitiesEnabled$ = brand$.pipe(
      map((brand) => !!brand.settings.billing?.enableBillingEntitySelection)
    );

    this.practiceLogoUrl$ = this.practice$.pipe(
      switchMap((practice) => this._logoUrl$(practice, this._storage))
    );

    this.logoUploadPath$ = this.practice$.pipe(
      map((practice) => Practice.logoStoragePath(practice))
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  get billingEntities(): TypedFormArray<IBillingEntity> {
    return this.form.controls.billingEntities as TypedFormArray<IBillingEntity>;
  }

  async printTest(): Promise<void> {
    const content: IPDFContent = {
      body: initVersionedSchema(GeneralPracticeHelpers.buildSampleContent()),
      documentSettings: this.documentSettingsForm.toDocumentSettings(),
    };

    await this.pdfService.print(content, []);
  }

  async save(): Promise<void> {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const practice = await snapshot(this.practice$);
    const changes = this.form.getRawValue();
    const documentSettings = this.documentSettingsForm.toDocumentSettings();

    await Firestore.saveDoc({
      ...practice,
      ...changes,
      address: changes.address?.address ?? '',
      metadata: {
        ...practice.metadata,
        address: changes.address,
      },
      settings: {
        ...practice.settings,
        ...changes.settings,
        print: {
          ...practice.settings?.print,
          documentSettings,
        },
      },
      slug: slugify(changes.name.toLowerCase()),
    });
    this._snackBar.open('Practice Updated');
  }

  async updateImageUrl(storagePath: string): Promise<void> {
    const practice = await snapshot(this.practice$);
    await Firestore.patchDoc(practice.ref, {
      branding: Practice.updateBrandingSettings(practice, {
        logoUrl: storagePath,
      }),
    });
  }

  async removeLogo(): Promise<void> {
    const practice = await snapshot(this.practice$);
    await Firestore.patchDoc(practice.ref, {
      branding: Practice.updateBrandingSettings(practice, {
        logoUrl: '',
      }),
    });
  }

  private async _resolveSelectedFeeScheduleLabel(
    scheduleGroups: IFeeScheduleGroup[],
    unselectedLabel: string,
    selectedRef?: DocumentReference<IFeeSchedule>
  ): Promise<string> {
    const label = this._getFeeScheduleLabel(scheduleGroups, selectedRef);
    if (label) {
      return label;
    }
    if (!selectedRef) {
      return unselectedLabel;
    }
    const staleSchedule = await Firestore.safeGetDoc(selectedRef);
    return staleSchedule
      ? `${staleSchedule.name} (Unavailable)`
      : unselectedLabel;
  }

  private _getFeeScheduleLabel(
    scheduleGroups: IFeeScheduleGroup[],
    feeScheduleRef?: DocumentReference<IFeeSchedule>
  ): string | undefined {
    if (!feeScheduleRef) {
      return;
    }
    const summaries = this._getFeeScheduleSummaries(scheduleGroups);
    const selected = summaries.find((summary) =>
      isSameRef(summary.feeScheduleRef, feeScheduleRef)
    );
    if (!selected) {
      return;
    }
    const scopeTitle =
      selected.scope !== FeeScheduleScope.Organisation
        ? `(${titlecase(selected.scope)})`
        : undefined;
    return compact([
      selected.scopeName,
      scopeTitle,
      `-`,
      selected.feeScheduleName,
    ]).join(' ');
  }

  private _getFeeScheduleSummaries(
    groups: IFeeScheduleGroup[]
  ): IFeeScheduleSummary[] {
    return groups.reduce((acc: IFeeScheduleSummary[], group) => {
      const groupSummaries = group.schedules.map((schedule) => ({
        scope: group.scope,
        scopeName: group.name,
        feeScheduleRef: schedule.ref,
        feeScheduleName: schedule.name,
      }));
      return [acc, groupSummaries].flat();
    }, []);
  }

  private _addressRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const address = control.value as IAddressMetadata;
      if (!address?.address) {
        return { addressRequired: true };
      }

      // eslint-disable-next-line no-null/no-null
      return null;
    };
  }

  private _websiteUrlValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (!value) {
        // eslint-disable-next-line no-null/no-null
        return null;
      }

      if (!VALID_URL.test(value)) {
        return { websiteUrl: true };
      }

      // eslint-disable-next-line no-null/no-null
      return null;
    };
  }

  private _logoUrl$(
    practice: WithRef<IPractice>,
    storage: Storage,
    size: ImageSize = ImageSize.Medium
  ): Observable<string | undefined> {
    if (!practice.branding?.logoUrl) {
      return of(undefined);
    }

    const logoRef = ref(
      storage,
      getImageAtSize(practice.branding.logoUrl, size, 'webp')
    );
    const fallbackRef = ref(storage, practice.branding.logoUrl);

    return from(getDownloadURL(logoRef)).pipe(
      catchError(() => getDownloadURL(fallbackRef)),
      shareReplayCold()
    );
  }
}
